import { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

/**
 * usePlatformFinanceSummary - Shared hook for fetching platform finance data
 * 
 * CANONICAL SOURCE: GET /api/platform-owner/finance/summary
 * Provider-aware: works with Airwallex (primary) or Stripe (legacy).
 * 
 * @param {number} rangeDays - Time range in days (7, 30, 90, 365)
 * @param {boolean} autoFetch - Whether to fetch automatically on mount (default: true)
 * @returns {{ data, loading, error, refetch }}
 */
export const usePlatformFinanceSummary = (rangeDays = 30, autoFetch = true) => {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refetch = useCallback(async (days = rangeDays) => {
    if (!api) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const res = await api.get(`/platform-owner/finance/summary?range_days=${days}`);
      setData(res.data);
      return res.data;
    } catch (err) {
      console.error('[usePlatformFinanceSummary] Error:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to fetch finance data');
      return null;
    } finally {
      setLoading(false);
    }
  }, [api, rangeDays]);

  useEffect(() => {
    if (autoFetch && api) {
      refetch(rangeDays);
    }
  }, [autoFetch, api, rangeDays, refetch]);

  return { data, loading, error, refetch };
};

/**
 * formatAUD - Format number as Australian Dollar currency
 * @param {number} amount 
 * @param {boolean} showDashOnNull - Show "—" instead of $0.00 when null/undefined (default: true)
 * @returns {string}
 */
export const formatAUD = (amount, showDashOnNull = true) => {
  if (amount === null || amount === undefined) {
    return showDashOnNull ? '—' : '$0.00';
  }
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
};

export default usePlatformFinanceSummary;
